// Sparks.
//
// Server events last exactly one tick, so the client keeps its own short-lived
// copy of each one and fades it out. Nothing here is authoritative; if a spark
// is dropped, nothing is wrong with the world.

import { PALETTE } from "../../content/palette";
import { EVENT, type EventKind } from "../../sim/types";
import type { EventView } from "../../shared/protocol";
import type { Camera } from "./scene";

interface Spark {
  kind: number;
  x: number;
  y: number;
  /** Seconds of life left, and how long it started with. */
  left: number;
  life: number;
}

/** How long each spark lives, and what colour it is. Keyed by name rather than
 *  by the wire integer, so adding an event kind means adding a row here that
 *  will not compile until it is a kind the simulation actually emits. */
const LIFETIMES: Record<EventKind, number> = {
  [EVENT.swordHit]: 0.22,
  [EVENT.enemyDied]: 0.4,
  [EVENT.playerHurt]: 0.3,
  [EVENT.playerDied]: 0.6,
  [EVENT.potionDrunk]: 0.5,
};

const COLOURS: Record<EventKind, string> = {
  [EVENT.swordHit]: PALETTE.slash,
  [EVENT.enemyDied]: PALETTE.enemyDeath,
  [EVENT.playerHurt]: PALETTE.hurt,
  [EVENT.playerDied]: PALETTE.hp,
  [EVENT.potionDrunk]: PALETTE.heal,
};

export class Fx {
  private sparks: Spark[] = [];

  add(events: readonly EventView[]): void {
    for (const event of events) {
      const life = LIFETIMES[event.k as EventKind] ?? 0.25;
      this.sparks.push({ kind: event.k, x: event.x, y: event.y, left: life, life });
    }
    // A player who tabs away for a minute comes back to a backlog of nothing;
    // a cap keeps a stalled frame loop from growing this without bound.
    if (this.sparks.length > 120) this.sparks.splice(0, this.sparks.length - 120);
  }

  step(dt: number): void {
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const spark = this.sparks[i]!;
      spark.left -= dt;
      if (spark.left <= 0) this.sparks.splice(i, 1);
    }
  }

  draw(ctx: CanvasRenderingContext2D, camera: Camera): void {
    for (const spark of this.sparks) {
      const t = spark.left / spark.life;
      const x = spark.x - camera.x;
      const y = spark.y - camera.y;
      ctx.save();
      ctx.globalAlpha = t;
      ctx.strokeStyle = COLOURS[spark.kind as EventKind] ?? PALETTE.text;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      // A death bursts outward; everything else is a tight ring at the point
      // of contact. Two shapes are enough to tell five events apart when the
      // colour is already doing most of the work.
      ctx.arc(x, y, spark.kind === EVENT.enemyDied || spark.kind === EVENT.playerDied ? 4 + (1 - t) * 12 : 3 + (1 - t) * 5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }
}
